import React, { useCallback, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import ComponentFunnel from '../../../../charts/echarts/ComponentFunnel'
import { useChartContext } from '../../../../charts/echarts/ChartProvider'
import { usePipelineDataContextWithVariables } from '../../provider/DDPDataProvider'
import { interceptFormatters } from '../ComponentBar/formatInterceptor'

const useFormatters = (valueFormat) => {
  const { formatter } = useFormattingContext()

  const valueFormatter = useCallback(d => formatter(d, valueFormat), [formatter, valueFormat])

  return {
    valueFormatter,
    formatter
  }
}

function DDPFunnel ({
  pipeline,
  variables,

  // Data structure configuration
  seriesAccessor = 'data', // Accessor to the data array
  nameAccessor, // Accessor to name field (e.g., 'stage', 'label', 'name')
  valueAccessor, // Accessor to value field (e.g., 'value', 'count', 'amount')

  // Formatting
  valueFormat = 'number',

  // All other props pass through to ComponentFunnel
  ...restProps
}) {
  const { valueFormatter } = useFormatters(valueFormat)
  const { setSeriesLoading } = useChartContext()
  const { data, isLoading } = usePipelineDataContextWithVariables(pipeline, variables)

  // Process data into funnel format
  const funnelData = useMemo(() => {
    if (!data) return []

    const rawData = seriesAccessor ? get(data, seriesAccessor) : data

    if (!Array.isArray(rawData)) return []
    if (!nameAccessor || !valueAccessor) return []

    return rawData.map(item => ({
      name: get(item, nameAccessor),
      value: Number(get(item, valueAccessor))
    }))
  }, [data, seriesAccessor, nameAccessor, valueAccessor])

  // Process props to intercept format strings
  const processedProps = useMemo(() => {
    return interceptFormatters({
      ...restProps
    }, valueFormatter, valueFormatter)
  }, [restProps, valueFormatter])

  // Set loading state
  useEffect(() => {
    setSeriesLoading(restProps.seriesId, isLoading)
  }, [isLoading, restProps.seriesId, setSeriesLoading])

  if (isLoading) return null

  return (
    <ComponentFunnel
      data={funnelData}
      {...processedProps}
    />
  )
}

DDPFunnel.propTypes = {
  // Pipeline configuration
  pipeline: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  variables: PropTypes.object,

  // Data structure configuration
  seriesAccessor: PropTypes.string,
  nameAccessor: PropTypes.string.isRequired,
  valueAccessor: PropTypes.string.isRequired,

  // Formatting
  valueFormat: PropTypes.string
}

DDPFunnel.defaultProps = {
  seriesAccessor: 'data',
  valueFormat: 'number'
}

export default DDPFunnel
